import React, { useEffect, useState } from 'react';
import { ILocation, VehicleTrackingMap } from './VehicleTrackingMap';
import { useSelector } from 'react-redux';
import { socket } from '../../utils/socket';
import { SOCKET_EVENTS } from '../views/utils';
import { IAppState } from '../../store';
import { Paper, Typography, Box } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

interface MapSectionProps {
  jobId: string;
}

const MapSection: React.FC<MapSectionProps> = ({ jobId }) => {
  const [eta, setEta] = useState<string | null>(null);
  const [distance, setDistance] = useState<string | null>(null);
  const [driverLocation, setDriverLocation] = useState<ILocation | null>(null);
  const [customerLocation, setCustomerLocation] = useState<ILocation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const authUser = useSelector((state: IAppState) => state.authReducer.authUser);

  useEffect(() => {
    // Subscribe to the driver location updates for the specific job
    socket.emit(SOCKET_EVENTS.SUBSCRIBE_DRIVER_LOCATION, { jobId });

    const handleDriverLocationUpdate = (data: any) => {
      const { driverLocation, customerLocation: customerLoc, eta, distance, error } = data;
      if (driverLocation) {
        setDriverLocation(driverLocation);
      }
      if (customerLoc) {
        setCustomerLocation(customerLoc);
      }
      setError(error);
      setEta(eta || 'Calculating...');
      setDistance(distance || 'Unknown');
    };

    // Listen for driver location updates
    socket.on(SOCKET_EVENTS.DRIVER_LOCATION_UPDATE, handleDriverLocationUpdate);

    // Cleanup: remove listener and disconnect the socket
    return () => {
      socket.off(SOCKET_EVENTS.DRIVER_LOCATION_UPDATE, handleDriverLocationUpdate);
      socket.disconnect();
    };
  }, [jobId]);

  return (
    <Paper elevation={3} style={{ padding: 16 }}>
      <Typography variant="h6" component="div" style={{ marginBottom: 16 }}>
        Vehicle Tracking
      </Typography>
      <Box
        style={{
          position: 'relative',
          height: 256,
          width: '100%',
          marginBottom: 16,
          backgroundColor: '#f0f0f0', // temporary background for debugging
          border: '1px solid #ccc',  // visual border to confirm container size
        }}
      >
        <VehicleTrackingMap
          driverLocation={driverLocation}
          customerLocation={customerLocation}
        />
      </Box>

      {(!error && eta) ? (
        <>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <AccessTimeIcon fontSize="small" color="primary" />
            <Typography variant="body1">
              Estimated Arrival: {eta || 'Calculating...'}
            </Typography>
          </Box>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            Distance: {distance || 'Unknown'}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" style={{ marginBottom: 16 }} color="textSecondary">
          Live location tracking data will be displayed here once available.
        </Typography>
      )}
    </Paper>
  );
};

export default MapSection;
